import { useCallback, useMemo, useState } from 'react';
import { message, Modal, Typography } from 'antd';
import {
  ExclamationCircleOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  PoweroffOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import {
  ErpDataGrid,
  ErpMasterShell,
  useErpMasterQuery
} from '../../../../components/common/erp-grid';
import { buildUomColumns } from './uomColumns';
import { uomApi } from './uomApi';
import UomModal from './UomModal';
import useAuth from '../../../../hooks/useAuth';

const { Title } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — UOM Master page (Stage 6B.8A-1).
//
// Server-mode grid with custom Add/Edit/View modal. Context menu
// adds Activate / Deactivate alongside View / Edit / Delete. System
// UOMs are protected — the Delete row action throws on the server,
// and the UI surfaces a friendly error.
//
// Permissions are read off the /me payload. The master role's list
// is `['*']` (wildcard); admins / managers / staff / viewer carry
// explicit `erp.masters.uoms.*` strings — see userPermissions.js.
// ─────────────────────────────────────────────────────────────────

const ADAPTER = { list: (query) => uomApi.list(query) };

function defaultOnError(err) {
  const msg = err?.response?.data?.error?.message ||
    err?.message || 'Failed to load UOMs.';
  message.error({ content: msg, key: 'uom-list' });
}

export default function UomMasterPage() {
  const { me } = useAuth();
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);
  const canCreate = hasPerm('erp.masters.uoms.create');
  const canUpdate = hasPerm('erp.masters.uoms.update');
  const canDelete = hasPerm('erp.masters.uoms.delete');

  const { gridHandlers, refetch, query } = useErpMasterQuery({
    adapter: ADAPTER, pageSize: 25, onError: defaultOnError
  });

  const currentPage = query && query.page     ? query.page     : 1;
  const pageSize    = query && query.pageSize ? query.pageSize : 25;
  const columns = useMemo(
    () => buildUomColumns({ currentPage, pageSize }),
    [currentPage, pageSize]
  );

  const [modalOpen, setModalOpen]     = useState(false);
  const [modalMode, setModalMode]     = useState('add');
  const [modalRecord, setModalRecord] = useState(null);

  const openAdd = useCallback(() => {
    setModalMode('add'); setModalRecord(null); setModalOpen(true);
  }, []);
  const openEditOrView = useCallback(async (record, mode) => {
    try {
      const full = await uomApi.get(record.id);
      setModalMode(mode); setModalRecord(full); setModalOpen(true);
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to load UOM.');
    }
  }, []);
  const openEdit = useCallback((record) => openEditOrView(record, 'edit'), [openEditOrView]);
  const openView = useCallback((record) => openEditOrView(record, 'view'), [openEditOrView]);
  const closeModal = useCallback(() => setModalOpen(false), []);
  const onSaved = useCallback(async () => {
    setModalOpen(false);
    await refetch();
  }, [refetch]);

  const handleStatusChange = useCallback(async (record, nextActive) => {
    try {
      await uomApi.updateStatus(record.id, { isActive: nextActive });
      message.success(nextActive ? 'UOM activated.' : 'UOM deactivated.');
      await refetch();
    } catch (err) {
      message.error(err?.response?.data?.error?.message || 'Failed to update status.');
    }
  }, [refetch]);

  const handleDelete = useCallback(async (record) => {
    if (record.isSystem) {
      message.warning('System UOMs cannot be deleted. Deactivate it instead.');
      return;
    }
    const confirmed = await new Promise((resolve) => {
      let okClicked = false;
      Modal.confirm({
        title: 'Delete UOM?',
        icon: <ExclamationCircleOutlined />,
        content: `${record.uomCode} · ${record.uomName} will be soft-deleted and hidden from pickers. Historical lines that reference its id are preserved.`,
        okText: 'Delete',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        onOk: () => { okClicked = true; },
        onCancel: () => { okClicked = false; },
        afterClose: () => resolve(okClicked)
      });
    });
    if (!confirmed) return;
    try {
      await uomApi.remove(record.id);
      message.success('UOM deleted.');
      await refetch();
    } catch (err) {
      message.error(err?.response?.data?.error?.message || 'Failed to delete UOM.');
    }
  }, [refetch]);

  const actionsProp = useMemo(() => ({
    add:    canCreate,
    edit:   canUpdate,
    view:   true,
    delete: canDelete
  }), [canCreate, canUpdate, canDelete]);

  const contextMenuActions = useMemo(() => {
    const items = [
      { key: 'view', label: 'View', icon: <EyeOutlined />, onClick: openView }
    ];
    if (canUpdate) {
      items.push({
        key: 'edit', label: 'Edit', icon: <EditOutlined />, onClick: openEdit
      });
      items.push({
        key: 'activate', label: 'Activate', icon: <CheckCircleOutlined />,
        onClick: (record) => handleStatusChange(record, true),
        // Show only when row is currently inactive.
        visible: (record) => record && record.isActive === false
      });
      items.push({
        key: 'deactivate', label: 'Deactivate', icon: <PoweroffOutlined />,
        onClick: (record) => handleStatusChange(record, false),
        visible: (record) => record && record.isActive === true
      });
    }
    if (canDelete) {
      items.push({
        key: 'delete', label: 'Delete', icon: <DeleteOutlined />, danger: true,
        onClick: handleDelete,
        // Hide on system rows — the server blocks the request anyway.
        visible: (record) => record && record.isSystem !== true
      });
    }
    return items;
  }, [canUpdate, canDelete, openView, openEdit, handleDelete, handleStatusChange]);

  return (
    <ErpMasterShell>
      {/* Stage UI-POLISH-LAYOUT-01 — internal padding via `.app-page-shell`. */}
      <div className="app-page-shell">
        <Title level={4} style={{ marginTop: 0, marginBottom: 8 }}>UOM Master</Title>
        <ErpDataGrid
          title="Units of Measurement"
          gridId="erp-masters-uoms-v1"
          columns={columns}
          rowKey="id"
          rowHeight="compact"
          stickyHeader
          searchable
          searchPlaceholder="Search by code / name / UQC…"
          searchShortcut
          exportable
          exportFilename="uom-master"
          actionWidth={56}
          actions={actionsProp}
          addButtonText="New UOM"
          addButtonIcon={<PlusOutlined />}
          onAdd={canCreate ? openAdd : undefined}
          onEdit={canUpdate ? openEdit : undefined}
          onView={openView}
          onRowClick={openView}
          onDelete={canDelete ? handleDelete : undefined}
          contextMenuActions={contextMenuActions}
          emptyTitle="No UOMs yet"
          emptyText="20 system UOMs are seeded with each tenant. If this list is empty, contact admin."
          emptyCtaLabel={canCreate ? 'New UOM' : undefined}
          {...gridHandlers}
        />
        <UomModal
          open={modalOpen}
          mode={modalMode}
          record={modalRecord}
          onClose={closeModal}
          onSaved={onSaved}
        />
      </div>
    </ErpMasterShell>
  );
}
